import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Card, Icon } from "semantic-ui-react";
import {
  formatDateRange,
  getLocale,
  getMoney,
  isoDate,
} from "../../../../utils/helperFunctions";
import useTheme from "../../../../hooks/useTheme";
import { PrimaryChip } from "../../../UI/CustomButtons";
import { AppColumn, AppRow } from "../../../UI/Layouts";
import { useResponsive } from "../../../UI/ResponsiveContext";
import { SizedBox } from "../../../UI/Sizes";
import AppText from "../../../UI/Texts/AppTexts";
import "./JobCard.css";
import { navigateToJobWithShareableId } from "../../../../utils/navigationFunctions";
import { withOpacity } from "../../../../utils/helperFunctions";
import { t } from "i18next";
import { getFeeMatrixForWorkplace } from "../../../../utils/feeFunctions";

const JobCard = ({ jobData, map = false, uniqueKey = "home" }) => {
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const core = useSelector((state) => state.core);

  const coreConstants = Object.freeze(core.constants);

  const feeConfigs = useSelector((state) => state.core.feeConfigs) ?? {};
  const staffingCompanyFeeData =
    feeConfigs[jobData?.workplaceDetails?.workPlaceId] ?? feeConfigs["default"];

  const displayCompensations =
    staffingCompanyFeeData?.displayCompensations ?? true;

  const staffingCompanyFee = staffingCompanyFeeData?.fee ?? 0;

  const workingHoursOptions = coreConstants.workingHours ?? [];
  const { currentUser } = useSelector((state) => state.auth);

  const isAuthenticated = currentUser != null;
  const allWorkfields = coreConstants.workfields ?? [];
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    if (isMobile || map) return;
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (isMobile || map) return;
    setIsHovered(false);
  };

  const healthcareWorkplaceDetails =
    jobData.healthcareWorkplaceDetails ?? jobData.workplaceDetails;
  const actualWorkplaceDetails = isAuthenticated
    ? healthcareWorkplaceDetails
    : (jobData.workplaceDetails ?? healthcareWorkplaceDetails);

  const jobCity =
    healthcareWorkplaceDetails.city &&
    healthcareWorkplaceDetails.city.length > 0
      ? healthcareWorkplaceDetails.city
      : healthcareWorkplaceDetails.municipality;

  const handleClick = (e) => {
    if (!jobLink) {
      e.preventDefault();
      navigateToJobWithShareableId(jobData);
    }
  };

  const professionTranslation = (() => {
    const roles = coreConstants.workroles ?? [];
    if (roles.length > 0) {
      return (
        roles.find((e) => e.id === jobData.profession)?.text[getLocale()] ??
        jobData.profession
      );
    }
    return jobData.profession;
  })();

  const profession =
    professionTranslation && professionTranslation.length > 0
      ? professionTranslation
      : jobData.profession;

  const emTypes = jobData.employmentTypes.map((emType) => {
    return (
      (coreConstants.employmentTypes ?? []).find((e) => e.id === emType)?.text[
        getLocale()
      ] ?? emType
    );
  });

  const timeOfDay = (jobData?.timeOfDay ?? []).map((id, index) => {
    const workingHoursData = workingHoursOptions.find((e) => e.id === id);
    const iconPath = `/assets/working_hours`;
    const iconId = workingHoursData?.iconId;
    return {
      id: workingHoursData?.id ?? `${index}`,
      icon: iconId ? `${iconPath}/${iconId}` : (workingHoursData?.icon ?? ""),
      text: workingHoursData?.text[getLocale()] ?? id,
    };
  });

  const baseFee = jobData?.baseFee?.amount ?? 0;

  const {
    canDisplayCompensation,
    text: compensationText,
    textColor,
    bgColor,
  } = getFeeMatrixForWorkplace({
    baseJobFeeAmount: baseFee,
    feeConfigs: feeConfigs,
    workplaceId: jobData?.workplaceDetails?.workPlaceId,
    hourlySuffix: "| h",
    
  });

  const compansationFee = baseFee - staffingCompanyFee;

  const startAt = jobData?.startAt ?? 0;
  const endAt = jobData?.endAt ?? 0;
  const showDateRange = startAt > 1 && endAt > 1;

  const divId = `job-card-${jobData?.jobId}-${uniqueKey}`;
  let jobContainerWidth =
    typeof document !== "undefined" ? document.body.clientWidth : 500;
  const jobReference =
    typeof document !== "undefined" && document.getElementById(divId);

  if (jobReference) {
    jobContainerWidth = jobReference.clientWidth;
  }

  const isSmall = isMobile || jobContainerWidth <= 600;
  const workfields = jobData.workFields ?? [];
  const jobLink = jobData?.linkUrl;

  return (
    <a
      id={divId}
      href={jobLink || "#"}
      target={jobLink ? "_blank" : "_self"}
      onClick={handleClick}
      style={{
        color: "inherit",
        textDecoration: "none",
        display: "block",
      }}
    >
      <Card
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          backgroundColor: theme.colors.white,
          width: "100%",
          cursor: "pointer",
          border: "1px solid rgba(0, 0, 0, 0.1)",
          borderRadius:
            map || isMobile ? theme.radius.small : theme.radius.default,
          padding: map || isMobile ? theme.spacing.elementPadding : theme.spacing.contentElementPadding,
          boxShadow: isHovered
            ? "0 10px 15px rgba(0, 0, 0, 0.2)"
            : "0 2px 6px rgba(0, 0, 0, 0.1)",
          transition: "box-shadow 0.3s ease-in-out",
        }}
      >
        {(() => {
          if (isSmall) {
            return (
              <AppColumn
                mainAxisAlignment="space-between"
                crossAxisAlignment="start"
                style={{
                  width: "100%",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: theme.spacing.elementPaddingSmall,
                }}
              >
                <AppRow crossAxisAlignment="center">
                  {jobData?.isEmergency === true && (
                    <span
                      title={t("jobs.emergency")}
                      aria-label={t("jobs.emergency")}
                      style={{
                        marginRight: theme.spacing.elementPaddingSmall,
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      <Icon
                        name="bolt"
                        style={{ color: theme.colors.red, margin: 0 }}
                      />
                    </span>
                  )}
                  <AppText.SubHeading
                    fontWeight={600}
                    use="strong"
                    margins={{
                      marginTop: "0px",
                      marginBottom: "0px",
                    }}
                    spans={[
                      {
                        text: `${jobCity}`,
                        className: "text-callout",
                        style: {
                          paddingLeft: theme.spacing.elementPaddingSmall,
                        },
                      },
                    ]}
                  >
                    {profession},
                  </AppText.SubHeading>
                </AppRow>

                {canDisplayCompensation && (
                  <AppRow
                    style={{
                      padding: "4px 8px",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "fit-content",
                      backgroundColor: bgColor,
                      borderRadius: "999rem",
                    }}
                  >
                    <AppText.Footnote
                      color={textColor}
                      fontWeight={400}
                      use="strong"
                      margins={{
                        marginTop: "0px",
                        marginBottom: "0px",
                      }}
                    >
                      {compensationText}
                    </AppText.Footnote>
                  </AppRow>
                )}
              </AppColumn>
            );
          } else {
            return (
              <AppRow
                mainAxisAlignment="space-between"
                crossAxisAlignment="start"
              >
                <AppRow crossAxisAlignment="center">
                  {jobData?.isEmergency === true && (
                    <span
                      title={t("jobs.emergency")}
                      aria-label={t("jobs.emergency")}
                      style={{
                        marginRight: theme.spacing.elementPaddingSmall,
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      <Icon
                        name="bolt"
                        style={{ color: theme.colors.red, margin: 0 }}
                      />
                    </span>
                  )}
                  <AppText.SubHeading
                    fontWeight={600}
                    use="strong"
                    margins={{
                      marginTop: "0px",
                      marginBottom: "0px",
                    }}
                    spans={[
                      {
                        text: `${jobCity}`,
                        className: "text-callout",
                        style: {
                          color: theme.colors.dark,
                          paddingLeft: theme.spacing.elementPaddingSmall,
                        },
                      },
                      {
                        text: jobData.sponsored === true ? "🏆" : "",
                        className: "text-callout",
                        style: {
                          color: theme.colors.dark,
                          paddingLeft: theme.spacing.elementPaddingSmall,
                        },
                      },
                    ]}
                    color={isHovered && theme.primaryColor}
                  >
                    {profession},
                  </AppText.SubHeading>
                </AppRow>
                {canDisplayCompensation && (
                  <AppRow
                    style={{
                      padding: "4px 8px",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "fit-content",
                      backgroundColor: bgColor,
                      borderRadius: "999rem",
                    }}
                  >
                    <AppText.Footnote
                      color={textColor}
                      fontWeight={400}
                      use="strong"
                      margins={{
                        marginTop: "0px",
                        marginBottom: "0px",
                      }}
                    >
                      {compensationText}
                    </AppText.Footnote>
                  </AppRow>
                )}
              </AppRow>
            );
          }
        })()}
        {(isAuthenticated || !jobData?.healthcareWorkplaceDetails) && (
          <AppColumn>
            <SizedBox height={theme.spacing.elementPaddingSmall} />
            <AppRow crossAxisAlignment="center">
              <AppText.Caption use="strong" margins={{ marginTop: "0px" }}>
                {[
                  ...new Set([
                    actualWorkplaceDetails.name,
                    actualWorkplaceDetails.nameVerksamhet,
                  ]),
                ].join(", ")}
              </AppText.Caption>
            </AppRow>
          </AppColumn>
        )}
        <SizedBox height={theme.spacing.elementPaddingSmall} />
        <AppText.Caption
          margins={{ marginTop: "0px" }}
          use="strong"
          spans={[
            {
              text:
                showDateRange &&
                ` |   ${formatDateRange(jobData.startAt, jobData.endAt)}`,
            },
          ]}
        >
          {emTypes.join(", ")}
        </AppText.Caption>
        <SizedBox height={theme.spacing.contentElementPadding} />
        <AppRow
          crossAxisAlignment="center"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: theme.spacing.elementPaddingSmall,
          }}
        >
          {timeOfDay.map((e) => (
            <PrimaryChip
              key={`${e.id}`}
              small
              icon={e.icon}
              text={e.text}
              secondary={true}
            />
          ))}
          {timeOfDay.length > 0 && (
            <AppText.SubHeading color={withOpacity(theme.colors.grey, "0.25")}>
              {"|"}
            </AppText.SubHeading>
          )}
          {workfields.length > 0 ? (
            workfields.map((wf, index) => {
              const iconPath = "/assets/workfields";
              const appWorkfield = allWorkfields.find((e) => e.id === wf);
              const iconId = appWorkfield?.iconId;
              const text = appWorkfield?.text[getLocale()] ?? wf;
              return (
                <PrimaryChip
                  key={`${wf}_${index}`}
                  small
                  basic
                  text={text}
                  icon={iconId ? `${iconPath}/${iconId}` : appWorkfield?.icon}
                  secondary={true}
                />
              );
            })
          ) : (
            <PrimaryChip
              key={`no-specialization`}
              small
              basic
              text={"Inget krav på specialisering"}
              secondary={true}
            />
          )}
        </AppRow>

        <AppColumn>
          <SizedBox height={theme.spacing.contentElementPadding} />
          <AppRow
            mainAxisAlignment="space-between"
            crossAxisAlignment="flex-end"
          >
            <AppColumn crossAxisAlignment="start">
              {jobData.workplaceDetails && (
                <AppText.Footnote>
                  {jobData.workplaceDetails.name}
                </AppText.Footnote>
              )}
              <SizedBox height={theme.spacing.elementPaddingSmall} />
              <AppText.Footnote color={theme.colors.grey}>
                {isoDate({
                  dateString: jobData.createdAt,
                  removeTime: true,
                })}
              </AppText.Footnote>
            </AppColumn>
            <div className="job-card-cta-button">
              <span>{t("jobs.view_details")}</span>
              <span className="job-card-cta-arrow">→</span>
            </div>
          </AppRow>
        </AppColumn>
      </Card>
    </a>
  );
};

export default JobCard;
